<template>
  <entry-point-page-template
    class="localized-text"
    :doctor-slug="doctorSlug"
    :hashed-data-id="hashedDataId"
  >
    <main-header :progress="progress" />
    <diagnostic-section :has-diagnostic="shouldDisplayYourSkinSection" />
    <personalized-treatment-section v-if="shouldDisplayPersonalizedTreatmentSection" />
    <your-routine-section />
    <your-skin-section v-if="shouldDisplayYourSkinSection" />
    <your-ingredients v-if="shouldDisplayYourIngredientsSection" />
    <making-serum-video-section class="making-serum-section" />
    <trustpilot-section />
    <we-in-media-section />
    <app-footer :action="footerAction" @action="onFooterAction" />

    <cart-modal />
    <nexultra-marketing-campaign-modal />
  </entry-point-page-template>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import debounce from 'lodash.debounce';

import MainHeader from '@/components/common/app-header/MainHeader.vue';
import DiagnosticSection from '@/components/diagnostic-section/DiagnosticSection.vue';
import YourRoutineSection from '@/components/your-routine/YourRoutineSection.vue';
import TrustpilotSection from '@/components/trustpilot-section/TrustpilotSection.vue';
import WeInMediaSection from '@/components/WeInMediaSection.vue';
import AppFooter from '@/components/AppFooter.vue';
import MakingSerumVideoSection from '@/components/video-section/MakingSerumVideoSection.vue';
import EntryPointPageTemplate from '@/views/EntryPointPageTemplate.vue';
import CartModal from '@/components/cart/CartModal.vue';
import NexultraMarketingCampaignModal from '@/components/nexultra-l-marketing-campaign/NexultraMarketingCampaignModal.vue';

import { isChromeBrowser } from '@/utils/common';

import { types } from '@/store/types';
import { PAGE_SECTION_NAME } from '@/constants/common';

export default {
  name: 'MainPage',
  components: {
    MainHeader,
    DiagnosticSection,
    YourRoutineSection,
    AppFooter,
    TrustpilotSection,
    WeInMediaSection,
    MakingSerumVideoSection,
    EntryPointPageTemplate,
    CartModal,
    NexultraMarketingCampaignModal,
    PersonalizedTreatmentSection: () =>
      import('@/components/personalized-treatment-section/PersonalizedTreatmentSection.vue'),
    YourIngredients: () => import('@/components/your-ingredients/YourIngredients.vue'),
    YourSkinSection: () => import('@/components/your-skin/YourSkinSection.vue')
  },
  inheritAttrs: false,
  props: {
    doctorSlug: {
      type: String,
      required: true
    },
    hashedDataId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      progress: 0
    };
  },
  computed: {
    ...mapState({
      isCartOpen: types.state.IS_CART_OPEN
    }),
    ...mapGetters({
      mainPageSections: types.getters.MAIN_PAGE_SECTIONS,
      cartProductsCount: types.getters.CART_PRODUCTS_COUNT
    }),
    pageSectionNames() {
      return this.mainPageSections.map(({ sectionName }) => sectionName);
    },
    shouldDisplayYourSkinSection() {
      return this.pageSectionNames.includes(PAGE_SECTION_NAME.YOUR_SKIN);
    },
    shouldDisplayYourIngredientsSection() {
      return this.pageSectionNames.includes(PAGE_SECTION_NAME.YOUR_INGREDIENTS);
    },
    shouldDisplayPersonalizedTreatmentSection() {
      return this.pageSectionNames.includes(PAGE_SECTION_NAME.YOUR_TREATMENT);
    },
    footerAction() {
      return this.cartProductsCount > 0 ? 'go-to-cart' : 'go-to-routine';
    }
  },
  async created() {
    window.addEventListener('scroll', this.debounceHandlePageScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.debounceHandlePageScroll);
  },
  methods: {
    ...mapMutations({
      toggleCart: types.mutations.TOGGLE_OPEN_CART
    }),
    onFooterAction(action) {
      if (action === 'go-to-cart') {
        this.toggleCart();

        return;
      }

      const scrollDuration = isChromeBrowser ? 300 : 700;

      this.$scrollTo('#your-routine', { duration: scrollDuration, offset: -40 });
    },
    debounceHandlePageScroll: debounce(function handlePageScroll() {
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;

      this.updatePageProgress(window.innerHeight, scrollTop);
    }, 100),
    updatePageProgress(windowHeight, scrollTop) {
      const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;

      this.progress = Math.round((scrollTop * 100) / (scrollHeight - windowHeight));
    }
  }
};
</script>

<style lang="scss" scoped>
.making-serum-section {
  margin-top: 50px;
}
</style>
